/**
 * Shared date formatting utilities for the portal referrals UI.
 */

/**
 * Format a date string to short month format: "Jan 15, 2025"
 * Used in table views.
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format a date string to long month format: "January 15, 2025"
 * Used in detail views.
 */
export const formatDateLong = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Format a date string to MM/DD/YYYY: "01/15/2025"
 * Used for date of birth display.
 */
export const formatDOB = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  if (!year || !month || !day) return '';
  return `${month}/${day}/${year}`;
};

/**
 * Format a date string to short date with time: "Jan 15, 2025 @ 3:00 PM"
 * Used for appointment and activity timestamps.
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' @ ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Format a date string as relative time with exact timestamp.
 * Returns { relative, exact } for two-line display:
 *   relative: "Just now", "5m ago", "2 days ago", "Mar 2"
 *   exact: "3:45 PM" (for today/recent) or "Mar 2 at 3:45 PM" (for older)
 */
export const formatRelativeTime = (dateString) => {
  if (!dateString) return { relative: '', exact: '' };
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  let relative;
  let exact;

  if (diffMin < 1) {
    relative = 'Just now';
    exact = timeStr;
  } else if (diffMin < 60) {
    relative = `${diffMin}m ago`;
    exact = timeStr;
  } else if (diffHr < 24) {
    relative = `${diffHr}h ago`;
    exact = timeStr;
  } else if (diffDays === 1) {
    relative = 'Yesterday';
    exact = timeStr;
  } else if (diffDays <= 6) {
    relative = `${diffDays} days ago`;
    exact = `${dateStr}, ${timeStr}`;
  } else if (diffDays <= 13) {
    relative = 'Over a week ago';
    exact = `${dateStr}, ${timeStr}`;
  } else if (diffDays <= 29) {
    const weeks = Math.floor(diffDays / 7);
    relative = `${weeks} weeks ago`;
    exact = `${dateStr}, ${timeStr}`;
  } else {
    const months = Math.floor(diffDays / 30);
    relative = months === 1 ? 'Over a month ago' : `${months} months ago`;
    exact = `${dateStr}, ${timeStr}`;
  }

  return { relative, exact };
};
